import { richTextFromMarkdown } from '@contentful/rich-text-from-markdown';
import debounce from 'lodash/debounce';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Box } from '../../components/box';
import { Column, Container, Row } from '../../components/layout/grid';
import { Policy } from '../../components/policy';
import type { PolicySectionsType } from '../../components/policy-sections';
import { PolicySections } from '../../components/policy-sections';
import { PolicySidebar } from '../../components/policy-sidebar';
import { TableOfContents } from '../../components/table-of-contents';
import { PageMeta } from '../../contentful-components/page-meta';
import { RichText } from '../../contentful-components/rich-text';
import type { DocumentWithLinks } from '../../contentful-components/rich-text/render';
import { getOrCreateContentfulClient } from '../../graphql/client';
import { firstFromCollection } from '../../graphql/collection';
import type { PageMetaFragment } from '../../graphql/fragments/PageMetaFragment.graphql';
import type { PolicyCategoryFragment } from '../../graphql/fragments/PolicyCategoryFragment.graphql';
import type { PolicyFragment } from '../../graphql/fragments/PolicyFragment.graphql';
import { getPageMeta } from '../../graphql/hooks/pageMeta';
import { getPolicies, getPolicyBySlug } from '../../graphql/hooks/policies';
import { useIsMobile } from '../../lib/hooks/useMedia';
import { useScroll } from '../../lib/hooks/useScroll';
import type { PageWithContext } from '../../lib/utils/context';
import { compact } from '../../lib/utils/omit';
import { getEnrichedPolicySections, policyMarkdownToRichText } from '../../lib/utils/policy';
import { isPrd } from '../../shared/util/env';
import { LINK } from '../../shared/util/link';
const POLICY_TTL = 60 * 5; // 5 minutes

const PolicyPage: PageWithContext<Props> = ({
  policy,
  meta,
  source,
  sections,
  categories
}) => {
  const {
    ref,
    inView
  } = useInView();
  const isMobile = useIsMobile();
  const [showSideBarTOC, setShowSidebarTOC] = useState(false);
  const {
    controller: scroll
  } = useScroll();
  useEffect(debounce(() => {
    if (!isMobile && !inView) {
      setShowSidebarTOC(true);
    } else {
      setShowSidebarTOC(false);
    }
  }, 250), [scroll.get().progress, isMobile, inView]);
  const {
    title = '',
    ...rest
  } = policy ?? {};
  return <Box pad={['vertical']} data-sentry-element="Box" data-sentry-component="PolicyPage" data-sentry-source-file="[slug].tsx">
            {meta?.title && <PageMeta id={meta.sys.id} name={title} meta={meta} />}
            <Container data-sentry-element="Container" data-sentry-source-file="[slug].tsx">
                <Row data-sentry-element="Row" data-sentry-source-file="[slug].tsx">
                    <Column span={{
          _: 12,
          lg: 8
        }} data-sentry-element="Column" data-sentry-source-file="[slug].tsx">
                        <Policy title={title ?? ''} {...rest} data-sentry-element="Policy" data-sentry-source-file="[slug].tsx">
                            <div ref={ref}>
                                <TableOfContents fixOnScroll={false} sections={sections} data-sentry-element="TableOfContents" data-sentry-source-file="[slug].tsx" />
                            </div>
                            {source && <RichText source={source} />}
                            <PolicySections sections={sections} data-sentry-element="PolicySections" data-sentry-source-file="[slug].tsx" />
                        </Policy>
                    </Column>
                    <Column span={{
          _: 12,
          lg: 4
        }} data-sentry-element="Column" data-sentry-source-file="[slug].tsx">
                        <PolicySidebar categories={categories} data-sentry-element="PolicySidebar" data-sentry-source-file="[slug].tsx" />
                        {showSideBarTOC && <TableOfContents title={title} fixOnScroll sections={sections} variant='sidebar' />}
                    </Column>
                </Row>
            </Container>
        </Box>;
};
export const getStaticPaths: GetStaticPaths = async () => {
  const policieCategories = await getPolicies(getOrCreateContentfulClient('/policies'));
  const paths = policieCategories?.data?.policyCategoryCollection?.items.map(categories => categories.policies?.items).flat().filter(policy => !policy?.externalLink && !(policy?.body?.type === 'PolicyAttachment' && policy?.body?.attachment?.contentType === 'application/pdf')).map(policy => policy?.slug ? `/policies/${policy?.slug}` : '').filter(Boolean) ?? [];
  return {
    paths: isPrd() ? paths : [],
    fallback: 'blocking'
  };
};
export const getStaticProps: GetStaticProps = async context => {
  const {
    slug
  } = context.params as {
    slug?: string;
  };
  if (!slug) {
    return {
      notFound: true
    };
  }
  const graphQLClient = getOrCreateContentfulClient(`/policies/${slug}`);
  const [policy, sidebarCategories] = await Promise.all([getPolicyBySlug(graphQLClient, {
    variables: {
      slug
    }
  }), getPolicies(graphQLClient)]);
  const item = firstFromCollection(policy?.data?.policyCollection, null);
  const categories = sidebarCategories?.data?.policyCategoryCollection?.items ?? [];
  if (!item) {
    return {
      notFound: true
    };
  }

  // fetch the meta data once we have the page
  const metaData = await getPageMeta(graphQLClient, {
    variables: {
      id: item?.meta?.sys.id ?? ''
    }
  });
  const {
    externalLink,
    body,
    sections
  } = item;

  // fetch all the markdown and rich text sectionds for the policy
  const response = await getEnrichedPolicySections(graphQLClient, sections?.items ?? []);

  // if policy has external link, redirect to that link
  if (externalLink) {
    return {
      props: {},
      redirect: {
        permanent: false,
        destination: externalLink
      }
    };
  }
  if (body?.type === 'Text') {
    return {
      props: {
        policy: item,
        meta: metaData.data.meta,
        source: compact(body?.content ?? {}, true),
        sections: response.sections ?? [],
        categories
      },
      revalidate: POLICY_TTL
    };
  }
  if (body?.type === 'PolicyContent') {
    if (!body?.body) {
      return {
        notFound: true
      };
    }
    const json = await policyMarkdownToRichText(body?.body);
    return {
      props: {
        policy: item,
        meta: metaData.data.meta,
        source: {
          json
        },
        sections: response.sections ?? [],
        categories
      },
      revalidate: POLICY_TTL
    };
  }
  if (!body && response.sections.length > 0) {
    return {
      props: {
        policy: item,
        meta: metaData.data.meta,
        source: null,
        sections: response.sections ?? [],
        categories
      },
      revalidate: POLICY_TTL
    };
  }
  if (!body?.attachment?.url) {
    return {
      notFound: true
    };
  }

  // if the content is markdown, we just need to render it the same as PolicyContent
  if (body?.attachment.contentType === 'text/markdown') {
    // if the policy content is an attachment, we need to download the actual content here
    const json = await fetch(body?.attachment.url).then(t => t.text()).then(richTextFromMarkdown);
    return {
      props: {
        policy: item,
        meta: metaData.data.meta,
        source: compact(json ?? {}, true),
        sections: response.sections ?? [],
        categories
      },
      revalidate: POLICY_TTL
    };
  }
  return {
    props: {},
    redirect: {
      permanent: false,
      destination: LINK.policyAttachment(slug).serialize()
    }
  };
};
type Props = {
  policy: PolicyFragment;
  meta: PageMetaFragment;
  source?: DocumentWithLinks;
  sections?: PolicySectionsType;
  categories?: PolicyCategoryFragment[];
};
export default PolicyPage;